// List of US states with slug and display name
export interface StateInfo {
  slug: string;
  name: string;
}

// Country metadata
export interface CountryInfo {
  slug: string; // URL-safe identifier, e.g. "united-states", "canada"
  name: string; // Display name, e.g. "United States", "Canada"
}

// Supported countries — United States first, Canada second (per clarification session 2026-02-07)
export const COUNTRIES: CountryInfo[] = [
  { slug: 'united-states', name: 'United States' },
  { slug: 'canada', name: 'Canada' },
  { slug: 'poland', name: 'Poland' },
  { slug: 'united-kingdom', name: 'United Kingdom' },
];

export const STATES: StateInfo[] = [
  { slug: 'alabama', name: 'Alabama' },
  { slug: 'alaska', name: 'Alaska' },
  { slug: 'arizona', name: 'Arizona' },
  { slug: 'arkansas', name: 'Arkansas' },
  { slug: 'california', name: 'California' },
  { slug: 'colorado', name: 'Colorado' },
  { slug: 'connecticut', name: 'Connecticut' },
  { slug: 'delaware', name: 'Delaware' },
  { slug: 'florida', name: 'Florida' },
  { slug: 'georgia', name: 'Georgia' },
  { slug: 'hawaii', name: 'Hawaii' },
  { slug: 'idaho', name: 'Idaho' },
  { slug: 'illinois', name: 'Illinois' },
  { slug: 'indiana', name: 'Indiana' },
  { slug: 'iowa', name: 'Iowa' },
  { slug: 'kansas', name: 'Kansas' },
  { slug: 'kentucky', name: 'Kentucky' },
  { slug: 'louisiana', name: 'Louisiana' },
  { slug: 'maine', name: 'Maine' },
  { slug: 'maryland', name: 'Maryland' },
  { slug: 'massachusetts', name: 'Massachusetts' },
  { slug: 'michigan', name: 'Michigan' },
  { slug: 'minnesota', name: 'Minnesota' },
  { slug: 'mississippi', name: 'Mississippi' },
  { slug: 'missouri', name: 'Missouri' },
  { slug: 'montana', name: 'Montana' },
  { slug: 'nebraska', name: 'Nebraska' },
  { slug: 'nevada', name: 'Nevada' },
  { slug: 'new-hampshire', name: 'New Hampshire' },
  { slug: 'new-jersey', name: 'New Jersey' },
  { slug: 'new-mexico', name: 'New Mexico' },
  { slug: 'new-york', name: 'New York' },
  { slug: 'north-carolina', name: 'North Carolina' },
  { slug: 'north-dakota', name: 'North Dakota' },
  { slug: 'ohio', name: 'Ohio' },
  { slug: 'oklahoma', name: 'Oklahoma' },
  { slug: 'oregon', name: 'Oregon' },
  { slug: 'pennsylvania', name: 'Pennsylvania' },
  { slug: 'puerto-rico', name: 'Puerto Rico' },
  { slug: 'rhode-island', name: 'Rhode Island' },
  { slug: 'south-carolina', name: 'South Carolina' },
  { slug: 'south-dakota', name: 'South Dakota' },
  { slug: 'tennessee', name: 'Tennessee' },
  { slug: 'texas', name: 'Texas' },
  { slug: 'utah', name: 'Utah' },
  { slug: 'vermont', name: 'Vermont' },
  { slug: 'virginia', name: 'Virginia' },
  { slug: 'washington', name: 'Washington' },
  { slug: 'west-virginia', name: 'West Virginia' },
  { slug: 'wisconsin', name: 'Wisconsin' },
  { slug: 'wyoming', name: 'Wyoming' },
];

export const stateNameToSlug = (name: string): string => {
  return name.toLowerCase().replace(/\s+/g, '-');
};

export const slugToStateName = (slug: string): string => {
  const found =
    STATES.find((s) => s.slug === slug) ||
    CA_PROVINCES.find((s) => s.slug === slug) ||
    PL_VOIVODESHIPS.find((s) => s.slug === slug) ||
    UK_NATIONS.find((s) => s.slug === slug);
  return found ? found.name : slug.replace(/-/g, ' ');
};

// Check if a slug corresponds to a valid state or province (any country)
export const isValidStateSlug = (slug: string | undefined): boolean => {
  if (!slug) return false;
  return (
    STATES.some((s) => s.slug === slug) ||
    CA_PROVINCES.some((s) => s.slug === slug) ||
    PL_VOIVODESHIPS.some((s) => s.slug === slug) ||
    UK_NATIONS.some((s) => s.slug === slug)
  );
};

// Approximate state centroids (latitude & longitude) for manual selection
export const STATE_CENTROIDS: Record<string, { lat: number; lon: number }> = {
  Alabama: { lat: 32.806671, lon: -86.79113 },
  Alaska: { lat: 61.370716, lon: -152.404419 },
  Arizona: { lat: 33.729759, lon: -111.431221 },
  Arkansas: { lat: 34.969704, lon: -92.373123 },
  California: { lat: 36.116203, lon: -119.681564 },
  Colorado: { lat: 39.059811, lon: -105.311104 },
  Connecticut: { lat: 41.597782, lon: -72.755371 },
  Delaware: { lat: 39.318523, lon: -75.507141 },
  Florida: { lat: 27.766279, lon: -81.686783 },
  Georgia: { lat: 33.040619, lon: -83.643074 },
  Hawaii: { lat: 21.094318, lon: -157.498337 },
  Idaho: { lat: 44.240459, lon: -114.478828 },
  Illinois: { lat: 40.349457, lon: -88.986137 },
  Indiana: { lat: 39.849426, lon: -86.258278 },
  Iowa: { lat: 42.011539, lon: -93.210526 },
  Kansas: { lat: 38.5266, lon: -96.726486 },
  Kentucky: { lat: 37.66814, lon: -84.670067 },
  Louisiana: { lat: 31.169546, lon: -91.867805 },
  Maine: { lat: 44.693947, lon: -69.381927 },
  Maryland: { lat: 39.063946, lon: -76.802101 },
  Massachusetts: { lat: 42.230171, lon: -71.530106 },
  Michigan: { lat: 43.326618, lon: -84.536095 },
  Minnesota: { lat: 45.694454, lon: -93.900192 },
  Mississippi: { lat: 32.741646, lon: -89.678696 },
  Missouri: { lat: 38.456085, lon: -92.288368 },
  Montana: { lat: 46.921925, lon: -110.454353 },
  Nebraska: { lat: 41.12537, lon: -98.268082 },
  Nevada: { lat: 38.313515, lon: -117.055374 },
  'New Hampshire': { lat: 43.452492, lon: -71.563896 },
  'New Jersey': { lat: 40.298904, lon: -74.521011 },
  'New Mexico': { lat: 34.840515, lon: -106.248482 },
  'New York': { lat: 42.165726, lon: -74.948051 },
  'North Carolina': { lat: 35.630066, lon: -79.806419 },
  'North Dakota': { lat: 47.528912, lon: -99.784012 },
  Ohio: { lat: 40.388783, lon: -82.764915 },
  Oklahoma: { lat: 35.565342, lon: -96.928917 },
  Oregon: { lat: 44.572021, lon: -122.070938 },
  Pennsylvania: { lat: 40.590752, lon: -77.209755 },
  'Puerto Rico': { lat: 18.220833, lon: -66.590149 },
  'Rhode Island': { lat: 41.680893, lon: -71.51178 },
  'South Carolina': { lat: 33.856892, lon: -80.945007 },
  'South Dakota': { lat: 44.299782, lon: -99.438828 },
  Tennessee: { lat: 35.747845, lon: -86.692345 },
  Texas: { lat: 31.054487, lon: -97.563461 },
  Utah: { lat: 40.150032, lon: -111.862434 },
  Vermont: { lat: 44.045876, lon: -72.710686 },
  Virginia: { lat: 37.769337, lon: -78.169968 },
  Washington: { lat: 47.400902, lon: -121.490494 },
  'West Virginia': { lat: 38.491226, lon: -80.954453 },
  Wisconsin: { lat: 44.268543, lon: -89.616508 },
  Wyoming: { lat: 42.755966, lon: -107.30249 },
};

// Canadian provinces (6 entries, alphabetically sorted — rural/sparse territories removed)
export const CA_PROVINCES: StateInfo[] = [
  { slug: 'alberta', name: 'Alberta' },
  { slug: 'british-columbia', name: 'British Columbia' },
  { slug: 'nova-scotia', name: 'Nova Scotia' },
  { slug: 'ontario', name: 'Ontario' },
  { slug: 'prince-edward-island', name: 'Prince Edward Island' },
  { slug: 'quebec', name: 'Quebec' },
];

// Polish voivodeships (16 entries, alphabetically by English name)
export const PL_VOIVODESHIPS: StateInfo[] = [
  { slug: 'greater-poland', name: 'Greater Poland' },
  { slug: 'holy-cross', name: 'Holy Cross' },
  { slug: 'kuyavia-pomerania', name: 'Kuyavia-Pomerania' },
  { slug: 'lesser-poland', name: 'Lesser Poland' },
  { slug: 'lodz', name: 'Łódź' },
  { slug: 'lower-silesia', name: 'Lower Silesia' },
  { slug: 'lublin', name: 'Lublin' },
  { slug: 'lubusz', name: 'Lubusz' },
  { slug: 'masovia', name: 'Masovia' },
  { slug: 'opole', name: 'Opole' },
  { slug: 'podlasie', name: 'Podlasie' },
  { slug: 'pomerania', name: 'Pomerania' },
  { slug: 'silesia', name: 'Silesia' },
  { slug: 'subcarpathia', name: 'Subcarpathia' },
  { slug: 'warmia-masuria', name: 'Warmia-Masuria' },
  { slug: 'west-pomerania', name: 'West Pomerania' },
];

// United Kingdom nations/jurisdictions (3 entries, alphabetically sorted)
export const UK_NATIONS: StateInfo[] = [
  { slug: 'england-and-wales', name: 'England and Wales' },
  { slug: 'northern-ireland', name: 'Northern Ireland' },
  { slug: 'scotland', name: 'Scotland' },
];

// Map of country slug → array of sub-national regions for that country
export const COUNTRY_STATES: Record<string, StateInfo[]> = {
  'united-states': STATES,
  canada: CA_PROVINCES,
  poland: PL_VOIVODESHIPS,
  'united-kingdom': UK_NATIONS,
};

// Approximate province centroids (latitude & longitude) for Canadian provinces
export const CA_PROVINCE_CENTROIDS: Record<string, { lat: number; lon: number }> = {
  Alberta: { lat: 55.0, lon: -115.0 },
  'British Columbia': { lat: 53.7, lon: -127.6 },
  'Nova Scotia': { lat: 44.7, lon: -63.0 },
  Ontario: { lat: 51.3, lon: -85.3 },
  'Prince Edward Island': { lat: 46.5, lon: -63.4 },
  Quebec: { lat: 52.9, lon: -71.8 },
};

// Approximate voivodeship centroids (latitude & longitude) for Polish voivodeships
export const PL_VOIVODESHIP_CENTROIDS: Record<string, { lat: number; lon: number }> = {
  'Greater Poland': { lat: 52.0, lon: 17.0 },
  'Holy Cross': { lat: 50.9, lon: 20.7 },
  'Kuyavia-Pomerania': { lat: 53.1, lon: 18.4 },
  'Lesser Poland': { lat: 49.8, lon: 20.1 },
  Łódź: { lat: 51.6, lon: 19.5 },
  'Lower Silesia': { lat: 51.1, lon: 16.9 },
  Lublin: { lat: 51.2, lon: 22.6 },
  Lubusz: { lat: 52.2, lon: 15.3 },
  Masovia: { lat: 51.8, lon: 20.6 },
  Opole: { lat: 50.6, lon: 17.9 },
  Podlasie: { lat: 53.2, lon: 22.9 },
  Pomerania: { lat: 54.2, lon: 18.0 },
  Silesia: { lat: 50.3, lon: 19.1 },
  Subcarpathia: { lat: 49.8, lon: 22.0 },
  'Warmia-Masuria': { lat: 53.9, lon: 20.5 },
  'West Pomerania': { lat: 53.5, lon: 15.4 },
};

// Approximate centroids (latitude & longitude) for UK nations
export const UK_NATION_CENTROIDS: Record<string, { lat: number; lon: number }> = {
  'England and Wales': { lat: 52.3555, lon: -1.1743 },
  'Northern Ireland': { lat: 54.7877, lon: -6.4923 },
  Scotland: { lat: 56.4907, lon: -4.2026 },
};

// State name to abbreviation mapping
export const STATE_ABBREVIATIONS: Record<string, string> = {
  Alabama: 'AL',
  Alaska: 'AK',
  Arizona: 'AZ',
  Arkansas: 'AR',
  California: 'CA',
  Colorado: 'CO',
  Connecticut: 'CT',
  Delaware: 'DE',
  Florida: 'FL',
  Georgia: 'GA',
  Hawaii: 'HI',
  Idaho: 'ID',
  Illinois: 'IL',
  Indiana: 'IN',
  Iowa: 'IA',
  Kansas: 'KS',
  Kentucky: 'KY',
  Louisiana: 'LA',
  Maine: 'ME',
  Maryland: 'MD',
  Massachusetts: 'MA',
  Michigan: 'MI',
  Minnesota: 'MN',
  Mississippi: 'MS',
  Missouri: 'MO',
  Montana: 'MT',
  Nebraska: 'NE',
  Nevada: 'NV',
  'New Hampshire': 'NH',
  'New Jersey': 'NJ',
  'New Mexico': 'NM',
  'New York': 'NY',
  'North Carolina': 'NC',
  'North Dakota': 'ND',
  Ohio: 'OH',
  Oklahoma: 'OK',
  Oregon: 'OR',
  Pennsylvania: 'PA',
  'Rhode Island': 'RI',
  'South Carolina': 'SC',
  'South Dakota': 'SD',
  Tennessee: 'TN',
  Texas: 'TX',
  Utah: 'UT',
  Vermont: 'VT',
  Virginia: 'VA',
  Washington: 'WA',
  'West Virginia': 'WV',
  Wisconsin: 'WI',
  Wyoming: 'WY',
};

export const getStateAbbreviation = (stateName: string): string => {
  return STATE_ABBREVIATIONS[stateName] || stateName.substring(0, 2).toUpperCase();
};

// --- Country helper functions ---

/** Check if a slug is a valid country */
export const isValidCountrySlug = (slug: string): boolean => COUNTRIES.some((c) => c.slug === slug);

/** Get states/provinces for a country slug */
export const getStatesForCountry = (countrySlug: string): StateInfo[] => COUNTRY_STATES[countrySlug] || [];

/** Given a state/province slug, find which country it belongs to */
export const getCountryForStateSlug = (stateSlug: string): string | undefined => {
  for (const [countrySlug, states] of Object.entries(COUNTRY_STATES)) {
    if (states.some((s) => s.slug === stateSlug)) {
      return countrySlug;
    }
  }
  return undefined;
};

/** Convert a country slug to display name */
export const countrySlugToName = (slug: string): string | undefined => COUNTRIES.find((c) => c.slug === slug)?.name;

/** Convert a country name to slug */
export const countryNameToSlug = (name: string): string | undefined =>
  COUNTRIES.find((c) => c.name.toLowerCase() === name.toLowerCase())?.slug;

/** Check if a state slug is valid within a specific country */
export const isValidStateSlugForCountry = (stateSlug: string, countrySlug: string): boolean =>
  (COUNTRY_STATES[countrySlug] || []).some((s) => s.slug === stateSlug);
